import { useMutation } from "react-query";

import type { MutationConfig } from "@/lib/react-query";
import { queryClient } from "@/lib/react-query";

import { fetchClient } from "@/lib/fetchClient";
import { isObject } from "../../../utils/typeGuard";
import type { Workflow } from "../types";

export type UpdateWorkflowDTO = {
  data: {
    workflow_name: string;
  };
  workflow_id: string;
};

async function updateWorkflow({ data, workflow_id }: UpdateWorkflowDTO) {
  await fetchClient.PUT("/workflows/{workflow_id}", {
    params: { path: { workflow_id } },
    body: data,
  });
}

type UseUpdateWorkflowOptions = {
  config?: MutationConfig<typeof updateWorkflow>;
};

export const useUpdateWorkflow = ({
  config,
}: UseUpdateWorkflowOptions = {}) => {
  return useMutation({
    onMutate: async (newWorkflow) => {
      await queryClient.cancelQueries("Workflows");

      const previousWorkflows =
        queryClient.getQueryData<Workflow[]>("Workflows");

      if (previousWorkflows) {
        queryClient.setQueryData(
          "Workflows",
          previousWorkflows.map((workflow) => {
            if (workflow.id === newWorkflow.workflow_id) {
              return {
                ...workflow,
                workflow_name: newWorkflow.data.workflow_name,
              };
            } else {
              return workflow;
            }
          })
        );
      }

      return { previousWorkflows };
    },
    onError: (_, __, context: unknown) => {
      if (isObject(context) && "previousWorkflows" in context) {
        queryClient.setQueryData("Workflows", context["previousWorkflows"]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries("Workflows");
    },
    ...config,
    mutationFn: updateWorkflow,
  });
};
